import { Box, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react'
import DOMPurify from 'dompurify'
import { useEffect, useRef, useState } from 'react'

import { FormModal } from '../../../../components/FormModal'
import { IBannerData } from '../../../../interfaces/Website/Banners/IBanners'
import { DEFAULT_TEXT_COLORS, tagStyle } from './siteColors'

interface IProps {
  banner: IBannerData
  desktopMediaType: string
  mobileMediaType: string
  isOpen: boolean
  onClose: () => void
}

// Espelho do banner da home do site (site-abra-v2): as cores padrão vêm de `siteColors.ts`, as
// medidas do card (padding, alturas, tamanhos de fonte) de `src/features/banner-card`, e os pesos
// do `GS.Typography` (título e tags em 500). Nada liga os dois lados: quem mudar o layout lá
// precisa atualizar esta prévia.

// A mesma conversão do site: o Quill grava parágrafos, mas o título e o botão recebem
// apenas conteúdo inline. O HTML é limpo antes de entrar na prévia.
function inlineHtml(value: string | undefined): string {
  if (!value) return ''

  if (!/<[a-z/!]/i.test(value)) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  const inline = value
    .replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, '<p></p>')
    .replace(/<\/p>\s*<p[^>]*>/gi, '<br>')
    .replace(/<\/?p[^>]*>/gi, '')
    .replace(/(<br\s*\/?>\s*)+$/i, '')

  return DOMPurify.sanitize(inline, {
    ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'u', 'span', 'br'],
    ALLOWED_ATTR: ['class', 'style']
  })
}

function useImageUrl(image: IBannerData['image']): string | undefined {
  const [url, setUrl] = useState<string>()

  useEffect(() => {
    if (!(image instanceof File)) {
      setUrl(typeof image === 'string' ? image : undefined)
      return
    }

    const objectUrl = URL.createObjectURL(image)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [image])

  return url
}

function PreviewViewport({ banner, mobile, mediaType }: { banner: IBannerData; mobile: boolean; mediaType: string }) {
  const frameWidth = mobile ? 390 : 1200
  const frameHeight = 420
  const container = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const desktopImage = useImageUrl(banner.image)
  const mobileImage = useImageUrl(banner.image_mobile)
  const image = mediaType === 'video' ? undefined : mobile ? mobileImage : desktopImage
  const video = mobile ? banner.video_url_mobile : banner.video_url

  useEffect(() => {
    const node = container.current
    if (!node) return

    const updateScale = () => setScale(Math.min(1, node.clientWidth / frameWidth))
    updateScale()

    const observer = new ResizeObserver(updateScale)
    observer.observe(node)
    return () => observer.disconnect()
  }, [frameWidth])

  const richTextStyles = {
    // O site não reseta o `strong`: vale o `bolder` do navegador, relativo ao peso do campo (700
    // no título e no subtítulo, 900 no botão). O reset do Chakra fixaria tudo em 700.
    '& strong, & b': { fontWeight: 'bolder' },
    '& .ql-bannerfont-montserrat': { fontFamily: "'Montserrat', sans-serif" },
    '& .ql-bannerfont-roboto': { fontFamily: "'Roboto', sans-serif" },
    '& .ql-bannerfont-georama': { fontFamily: "'Georama', sans-serif" },
    '& .ql-size-small': { fontSize: '0.75em' },
    '& .ql-size-large': { fontSize: '1.5em', lineHeight: 1.1 },
    '& .ql-size-huge': { fontSize: '2.5em', lineHeight: 1.1 }
  }

  return (
    <Box ref={container} w="100%">
      <Box
        h={`${frameHeight * scale}px`}
        overflow="hidden"
        borderRadius={mobile ? '0 0 22px 22px' : '22px'}
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Box
          position="relative"
          w={`${frameWidth}px`}
          h={`${frameHeight}px`}
          transform={`scale(${scale})`}
          transformOrigin="top left"
          overflow="hidden"
          bg="white"
          backgroundImage={image ? `url(${JSON.stringify(image)})` : undefined}
          backgroundSize="cover"
          backgroundPosition="center right"
          _after={
            banner.gradient_color
              ? {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  pointerEvents: 'none',
                  background: `linear-gradient(to right, ${banner.gradient_color} 40%, transparent)`
                }
              : undefined
          }
        >
          <Flex align="center" h="100%" maxW="55%" p={mobile ? '0 0 0 20px' : '75px'} position="relative" zIndex={2}>
            <Box ml={mobile ? '15px' : 0} mt={mobile ? '125px' : 0} mb={mobile ? '130px' : 0}>
              <Flex wrap={mobile ? 'wrap' : 'nowrap'}>
                {(banner.tags ?? []).map((tag) => {
                  const style = tagStyle(banner, tag)
                  return (
                    <Flex
                      key={tag}
                      align="center"
                      justify="center"
                      flexShrink={0}
                      w={`${style.width}px`}
                      maxW={mobile ? '140px' : '500px'}
                      h="28px"
                      mr="15px"
                      mb="15px"
                      borderRadius="17px"
                      bg={style.background ?? 'transparent'}
                      color={style.color}
                      border={style.border}
                      fontFamily="'Montserrat', sans-serif"
                      fontWeight={500}
                      fontSize="13px"
                      textTransform="uppercase"
                    >
                      {tag}
                    </Flex>
                  )
                })}
              </Flex>
              {/* `div`, e não o `h2` do site: o `index.html` do painel força negrito com `!important`
                  em todo heading, e o título inteiro sairia em negrito. */}
              <Box
                as="div"
                m={0}
                color={banner.title_color || DEFAULT_TEXT_COLORS.title}
                fontFamily="'Montserrat', sans-serif"
                fontWeight={500}
                fontSize={mobile ? '22px' : '40px'}
                lineHeight={1.2}
                overflowWrap="break-word"
              >
                <Box as="span" sx={richTextStyles} dangerouslySetInnerHTML={{ __html: inlineHtml(banner.title) }} />
              </Box>
              <Box
                as="p"
                mt={mobile ? '20px' : '15px'}
                mb="15px"
                maxW="80%"
                color={banner.subtitle_color || DEFAULT_TEXT_COLORS.subtitle}
                fontFamily="'Roboto', sans-serif"
                fontWeight={400}
                fontSize={mobile ? '16px' : '19px'}
                lineHeight={1.2}
                overflowWrap="break-word"
              >
                <Box as="span" sx={richTextStyles} dangerouslySetInnerHTML={{ __html: inlineHtml(banner.subtitle) }} />
              </Box>
              {!!banner.cta_link?.trim() && (
                <Box
                  display="inline-block"
                  px="24px"
                  py="12px"
                  borderRadius="4px"
                  bg={banner.button_color || 'transparent'}
                  color={banner.button_text_color || DEFAULT_TEXT_COLORS.buttonText}
                  fontFamily="'Montserrat', sans-serif"
                  fontWeight={600}
                  fontSize="14px"
                  textTransform="uppercase"
                >
                  <Box
                    as="span"
                    sx={richTextStyles}
                    dangerouslySetInnerHTML={{ __html: inlineHtml(banner.cta_text) }}
                  />
                </Box>
              )}
            </Box>
          </Flex>
        </Box>
      </Box>
      {!image && (
        <Text variant="caption" mt={2}>
          {mediaType === 'video' && video
            ? 'O site ainda não exibe o vídeo como fundo deste banner.'
            : 'Sem imagem para esta versão.'}
        </Text>
      )}
    </Box>
  )
}

export default function BannerPreview({ banner, desktopMediaType, mobileMediaType, isOpen, onClose }: IProps) {
  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Prévia do banner" size="editor">
      <Text variant="caption" mb={4}>
        Esta prévia mostra as alterações atuais, mesmo antes de salvar.
      </Text>
      <Tabs isLazy lazyBehavior="keepMounted">
        <TabList>
          <Tab>Desktop · 1200 × 420</Tab>
          <Tab>Mobile · 390 × 420</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <PreviewViewport banner={banner} mobile={false} mediaType={desktopMediaType} />
          </TabPanel>
          <TabPanel px={0}>
            <PreviewViewport banner={banner} mobile mediaType={mobileMediaType} />
          </TabPanel>
        </TabPanels>
      </Tabs>
      {!banner.is_gradient_active && !!banner.gradient_color && (
        <Text variant="caption" mt={2}>
          O site ainda mostra a cor do gradiente mesmo com a opção desligada.
        </Text>
      )}
    </FormModal>
  )
}
